import { Bar, BarChart, CartesianGrid, Cell, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis } from "recharts";
import { fmtDuree, fmtNum } from "@/lib/format";
import { Panel } from "@/components/common/Panel";

const AXE = { stroke: "#7d8899", fontSize: 10 };
const TOOLTIP = { contentStyle: { background: "#0f172a", border: "1px solid #334155", fontSize: 11 } };
const COULEURS = ["#1c64f2", "#0e9f6e", "#d97706", "#e02424", "#8b5cf6", "#0891b2"];

export const GrapheTemps = ({ titre, data, series, testId, actions, hauteur = 220, format = fmtDuree }) => (
  <Panel title={titre} testId={testId} actions={actions} bodyClassName="p-2">
    <ResponsiveContainer width="100%" height={hauteur}>
      <BarChart data={data}>
        <CartesianGrid stroke="#1e293b" vertical={false} />
        <XAxis dataKey="label" tick={AXE} />
        <YAxis tick={AXE} tickFormatter={format} />
        <Tooltip {...TOOLTIP} formatter={(v) => format(v)} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {series.map((s, i) => (
          <Bar key={s.cle} dataKey={s.cle} name={s.label} stackId={s.stack} fill={s.couleur || COULEURS[i]} />
        ))}
      </BarChart>
    </ResponsiveContainer>
  </Panel>
);

export const GrapheLignes = ({ titre, data, series, testId, actions, hauteur = 240 }) => (
  <Panel title={titre} testId={testId} actions={actions} bodyClassName="p-2">
    <ResponsiveContainer width="100%" height={hauteur}>
      <LineChart data={data}>
        <CartesianGrid stroke="#1e293b" vertical={false} />
        <XAxis dataKey="label" tick={AXE} />
        <YAxis tick={AXE} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
        <Tooltip {...TOOLTIP} formatter={(v) => (v == null ? "—" : `${v} %`)} />
        <Legend wrapperStyle={{ fontSize: 10 }} />
        {series.map((s, i) => (
          <Line key={s.cle} type="monotone" dataKey={s.cle} name={s.label} stroke={s.couleur || COULEURS[i]}
            strokeWidth={s.cle === "trs" ? 2.5 : 1.5} dot={false} connectNulls />
        ))}
      </LineChart>
    </ResponsiveContainer>
  </Panel>
);

/** Pareto : barres décroissantes + courbe de cumul. Clic sur une barre = drill-down. */
export const GraphePareto = ({ titre, data, cleValeur, format = fmtDuree, testId, actions,
                               onClic, selection, top = 5 }) => {
  const limite = top === "tout" ? data.length : top;
  const items = data.slice(0, limite);
  return (
    <Panel title={titre} testId={testId} actions={actions} bodyClassName="p-2">
      <ResponsiveContainer width="100%" height={230}>
        <BarChart data={items}>
          <CartesianGrid stroke="#1e293b" vertical={false} />
          <XAxis dataKey="nom" tick={AXE} interval={0} angle={-12} textAnchor="end" height={44} />
          <YAxis yAxisId="l" tick={AXE} tickFormatter={format} />
          <YAxis yAxisId="r" orientation="right" tick={AXE} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
          <Tooltip {...TOOLTIP} formatter={(v, n) => (n === "% cumulé" ? `${v} %` : format(v))} />
          <Bar yAxisId="l" dataKey={cleValeur} name="Durée / quantité" onClick={(d) => onClic?.(d)} cursor={onClic ? "pointer" : "default"}>
            {items.map((e, i) => (
              <Cell key={i} fill={selection && selection === e.nom ? "#facc15" : (e.planifie ? "#0891b2" : COULEURS[i % COULEURS.length])} />
            ))}
          </Bar>
          <Line yAxisId="r" type="monotone" dataKey="cumul_pct" name="% cumulé" stroke="#facc15" strokeWidth={2} dot={false} />
        </BarChart>
      </ResponsiveContainer>
      <div className="overflow-x-auto mt-1">
        <table className="w-full text-xs">
          <thead className="bg-muted/40"><tr className="text-left eyebrow">
            <th className="px-2 py-1">Cause</th><th className="px-2 py-1 text-right">Valeur</th>
            <th className="px-2 py-1 text-right">Occurrences</th><th className="px-2 py-1 text-right">% du total</th>
            <th className="px-2 py-1 text-right">% cumulé</th></tr></thead>
          <tbody className="divide-y divide-border/50 font-mono">
            {items.map((e) => (
              <tr key={e.nom} data-testid={`${testId}-row`} onClick={() => onClic?.(e)}
                className={onClic ? "cursor-pointer hover:bg-muted/20" : ""}>
                <td className="px-2 py-1">{e.nom}</td>
                <td className="px-2 py-1 text-right">{format(e[cleValeur])}</td>
                <td className="px-2 py-1 text-right">{fmtNum(e.occurrences)}</td>
                <td className="px-2 py-1 text-right">{e.pct_du_total} %</td>
                <td className="px-2 py-1 text-right text-amber-400">{e.cumul_pct} %</td>
              </tr>
            ))}
            {!items.length && <tr><td colSpan={5} className="px-2 py-3 text-center text-muted-foreground">Aucune donnée</td></tr>}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

/** Classement horizontal (arrêts par machine, rejets par machine…). Clic = drill-down. */
export const Classement = ({ titre, data, cleValeur, format = fmtDuree, testId, onClic, selection }) => (
  <Panel title={titre} testId={testId} bodyClassName="p-2 space-y-1">
    {data.map((e) => {
      const max = Math.max(...data.map((x) => x[cleValeur] || 0)) || 1;
      return (
        <button key={e.nom} data-testid={`${testId}-item`} onClick={() => onClic?.(e)}
          className="w-full text-left group" disabled={!onClic}>
          <div className="flex items-center justify-between text-[11px] font-mono">
            <span className={selection === e.nom ? "text-amber-400" : "group-hover:text-primary"}>{e.nom}</span>
            <span className="text-muted-foreground">{format(e[cleValeur])}{e.occurrences != null && ` · ${e.occurrences}`}</span>
          </div>
          <div className="h-1.5 bg-muted/40 rounded-sm overflow-hidden">
            <div className="h-full bg-primary/70 group-hover:bg-primary transition-all"
              style={{ width: `${((e[cleValeur] || 0) / max) * 100}%` }} />
          </div>
        </button>
      );
    })}
    {!data.length && <div className="text-xs text-muted-foreground px-1 py-2">Aucune donnée</div>}
  </Panel>
);

export const GrapheQuantites = ({ titre, serie, unite, testId, actions }) => {
  const data = serie.map((p) => ({ label: p.label, bonnes: p.par_unite?.[unite]?.bonnes ?? p.par_unite?.[unite] ?? 0,
    rejets: p.par_unite?.[unite]?.rejets ?? 0 }));
  return (
    <GrapheTemps titre={titre} data={data} testId={testId} actions={actions} format={(v) => fmtNum(v)}
      series={[{ cle: "bonnes", label: `Production bonne (${unite})`, stack: "a", couleur: "#0e9f6e" },
               { cle: "rejets", label: `Rejets (${unite})`, stack: "a", couleur: "#e02424" }]} />
  );
};
